import { Component, Element, h, Host, Prop, State, Watch } from '@stencil/core'

import Helpers from '../../utils/helpers'
import { CssClassMap } from '../../utils/interfaces'

/**
 * ## Design System
 *
 * Para a documentação completa de design, incluindo diretrizes de uso, acessibilidade e exemplos visuais, consulte o [Design System do GovBR](https://www.gov.br/ds/components/avatar?tab=designer).
 */
@Component({
  tag: 'br-avatar',
  styleUrl: 'avatar.scss',
  shadow: true,
})
export class Avatar {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento do DOM associado ao componente.
   */
  @Element() el: HTMLBrAvatarElement

  /**
   * Ajusta a densidade, alterando o espaçamento interno para um visual mais compacto ou mais expandido.
   */
  @Prop({ mutable: true }) density: 'small' | 'medium' | 'large' = 'medium'

  /**
   * Identificador único; gerado automaticamente quando omitido.
   */
  @Prop({ reflect: true }) readonly customId: string = Helpers.generateUniqueId('br-avatar')

  /**
   * URL da imagem a ser exibida no avatar do tipo 'fotográfico'.
   * Deve ser uma URL válida que aponta para a imagem desejada.
   */
  @Prop() readonly src: string

  /**
   * Texto alternativo (alt) associado à imagem do avatar. Essencial para acessibilidade.
   * Deve descrever de forma clara e concisa o conteúdo da imagem, por exemplo: "Foto de perfil de João Silva".
   */
  @Prop() readonly alt = 'Foto de perfil do usuário'

  /**
   * Força a exibição do ícone padrão, sobrescreve outras opções (texto ou imagem).
   * Útil para manter consistência visual ou quando se deseja um avatar neutro independente do conteúdo disponível.
   */
  @Prop() readonly isIconic: boolean = false

  /**
   * Conteúdo textual do avatar.
   * Apenas o primeiro caractere será exibido em maiúscula.
   */
  @Prop() readonly text: string

  /**
   * Define se o avatar está desabilitado
   */
  @Prop() readonly disabled = false

  /**
   * Permite definir a cor de fundo do componente.
   * Aceita os seguintes formatos de cor:
   * - Cores nomeadas do CSS: 'red', 'blue', 'green', 'yellow', etc.
   * - Códigos hexadecimais: '#ff0000', '#00ff00', '#0000ff', etc.
   * - Valores RGB: 'rgb(255, 0, 0)', 'rgb(0, 255, 0)', etc.
   * - Valores RGBA: 'rgba(255, 0, 0, 0.5)', 'rgba(0, 255, 0, 0.8)', etc.
   * - Valores HSL: 'hsl(0, 100%, 50%)', 'hsl(120, 100%, 50%)', etc.
   * - Valores HSLA: 'hsla(0, 100%, 50%, 0.5)', 'hsla(240, 100%, 50%, 0.7)', etc.
   * Se não especificada, usa a cor padrão do tema.
   */
  @Prop({ reflect: true }) readonly bgColor: string = '#DBE8FB'

  @State() private hasError = false

  /**
   * Observa mudanças na propriedade density para validação e sincronização com atributo DOM.
   * Garante que apenas valores válidos sejam aceitos e atualiza o atributo no elemento host.
   */
  @Watch('density')
  handleDensityChange(newValue: 'small' | 'medium' | 'large') {
    if (!['small', 'medium', 'large'].includes(newValue)) {
      console.warn(`Valor inválido para density: ${newValue}. Usando 'medium'.`)
      this.density = 'medium'
    }
    this.el.setAttribute('density', this.density)
  }

  componentWillLoad() {
    this.handleDensityChange(this.density)
    this.setCustomBackgroundColor()
  }

  componentDidUpdate() {
    this.setCustomBackgroundColor()
  }

  /**
   * Gera um mapa de classes CSS com base nas propriedades do componente.
   * @returns {CssClassMap} Objeto contendo as classes CSS aplicadas.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-avatar': true,
      [this.density]: true,
      error: this.hasError,
      disabled: this.disabled,
    }
  }

  /**
   * Verifica se uma string possui conteúdo válido (não vazia após remover espaços em branco).
   * @param value - String a ser verificada
   * @returns {boolean} true se a string possui conteúdo
   */
  private hasContent(value: string): boolean {
    return value?.trim().length > 0
  }

  private readonly handleImageError = () => {
    this.hasError = true
    console.error(`Erro ao carregar imagem do avatar: ${this.src}`)
  }

  /**
   * Obtém as classes CSS para o elemento content.
   * @returns {string} Classes CSS do content
   */
  private getContentClasses(): string {
    if (this.hasContent(this.text) && !this.isIconic) {
      return 'content text-pure-0'
    }
    return 'content'
  }

  /**
   * Define a variável CSS para cor de fundo personalizada para o avatar texto.
   */
  private setCustomBackgroundColor() {
    if (this.hasContent(this.bgColor)) {
      this.el.style.setProperty('--avatar-text-background-color', this.bgColor)
    }
  }

  /**
   * Gera aria-label apropriado baseado no tipo de avatar
   * Prioriza aria-label do host, senão gera automaticamente
   */
  private getAriaLabel(): string {
    const hostAriaLabel = this.el.getAttribute('aria-label')
    if (this.hasContent(hostAriaLabel)) {
      return hostAriaLabel
    }

    if (this.hasContent(this.text) && !this.isIconic) {
      return `Avatar de ${this.text}`
    }

    if (this.hasContent(this.src) && !this.hasError) {
      return this.alt
    }

    return 'Avatar com ícone padrão de usuário'
  }

  /**
   * Obtém o valor do token do DS para altura do ícone
   */
  private getIconSize(): string {
    const tokenName = `--icon-size-${this.density === 'small' ? '2x' : this.density === 'medium' ? '5x' : '8x'}`
    return getComputedStyle(this.el).getPropertyValue(tokenName).trim()
  }

  /**
   * Obtém o valor do token do DS para largura do ícone (altura + 8px)
   */
  private getIconWidth(): string {
    const baseSize = this.getIconSize()
    const numericValue = parseInt(baseSize.replace('px', ''))
    return `${numericValue + 8}px`
  }

  /**
   * Renderiza o avatar do tipo 'letra/texto'.
   * Exibe a primeira letra do texto em maiúscula.
   */
  private renderTextAvatar() {
    return this.text[0].toUpperCase()
  }

  /**
   * Renderiza o avatar do tipo 'fotográfico/imagem'.
   * Exibe uma imagem carregada via URL.
   */
  private renderImageAvatar() {
    return <img src={this.src} alt={this.alt} loading="lazy" onError={this.handleImageError} />
  }

  /**
   * Renderiza o avatar do tipo 'icônico'.
   * Exibe um ícone padrão (fa-solid:user) posicionado na base do avatar.
   */
  private renderIconAvatar() {
    return (
      <br-icon
        icon-name="fa-solid:user"
        height={this.getIconSize()}
        width={this.getIconWidth()}
        aria-hidden="true"
        class="avatar-icon"
      ></br-icon>
    )
  }

  /**
   * Método principal de renderização do avatar.
   * Prioridade: isIconic > texto > imagem > ícone (padrão)
   */
  private renderAvatar() {
    if (this.isIconic) {
      return this.renderIconAvatar()
    }

    if (this.hasContent(this.text)) {
      return this.renderTextAvatar()
    }

    if (this.hasContent(this.src) && !this.hasError) {
      return this.renderImageAvatar()
    }

    return this.renderIconAvatar()
  }

  render() {
    return (
      <Host>
        <span id={this.customId} class={this.getCssClassMap()} role="img" aria-label={this.getAriaLabel()}>
          <span class={this.getContentClasses()}>{this.renderAvatar()}</span>
        </span>
      </Host>
    )
  }
}
